import { useCallback, useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import AnalysisOutput from "@/components/AnalysisOutput";
import PressKit from "@/components/PressKit";
import OnboardingModal from "@/components/OnboardingModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLang } from "@/contexts/LangContext";
import { api, streamAnalysis } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Mic, Upload, Trash2, Eye, Loader2, Newspaper, Search, GitCompareArrows, X } from "lucide-react";

const MODELS = [
  { id: "claude", label: "Claude Sonnet 4.5", code: "ANTHROPIC" },
  { id: "openai", label: "GPT-5.2", code: "OPENAI" },
  { id: "gemini", label: "Gemini 3 Pro", code: "GOOGLE" },
];

/** Escape regex special characters in a user-provided string. */
function _escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Highlight search terms inside a snippet. Returns an array of React nodes.
 * - Tokenizes the query by whitespace, drops tokens shorter than 3 chars and surrounding quotes
 * - Falls back to the raw query if no usable tokens
 * - Case-insensitive match; no regex injection (terms are escaped)
 */
function highlightSnippet(snippet, query) {
  if (!snippet) return null;
  const q = (query || "").trim();
  if (!q) return snippet;
  const tokens = q
    .split(/\s+/)
    .map((t) => t.replace(/^"|"$/g, ""))
    .filter((t) => t.length >= 3);
  const terms = tokens.length > 0 ? tokens : [q];
  const pattern = new RegExp(`(${terms.map(_escapeRe).join("|")})`, "gi");
  const parts = snippet.split(pattern);
  const lowerTerms = new Set(terms.map((t) => t.toLowerCase()));
  return parts.map((part, i) =>
    lowerTerms.has(part.toLowerCase()) ? (
      <mark
        key={i}
        className="bg-[#FFE4E6] text-[#D90429] font-semibold px-0.5 rounded-sm"
      >
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("text"); // text | file | audio
  const [text, setText] = useState("");
  const [model, setModel] = useState("claude");
  const [analysis, setAnalysis] = useState("");
  const [analysisId, setAnalysisId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pressKit, setPressKit] = useState(null);
  const [pressLoading, setPressLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [compareSet, setCompareSet] = useState([]); // [id1, id2]
  const [availableModels, setAvailableModels] = useState([]);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const outRef = useRef(null);
  const nav = useNavigate();

  // Load controlled list of analyze models + estimated cost (no hardcoded prices in code)
  useEffect(() => {
    let cancelled = false;
    api
      .get("/analyze/models")
      .then((r) => {
        if (cancelled) return;
        setAvailableModels(r.data?.models || []);
      })
      .catch(() => {
        if (cancelled) return;
        // graceful fallback — keep selector minimal without costs
        setAvailableModels([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Synthetic sample speech — NOT a real politician's words. Pure rhetorical scaffolding
  // so the user can experience the analysis without external content.
  const SAMPLE_TEXT_ES =
    "Mis estimados compatriotas: hoy enfrentamos una hora decisiva. No estamos solos: detrás de cada decisión que tomamos hay enemigos del pueblo que trabajan en la sombra para frenar nuestro avance. El miedo no detendrá lo que ya empezamos. La grandeza de esta nación está en su gente trabajadora, no en los que ocupan los despachos y nos quieren imponer un modelo extranjero. Por eso les digo: no vamos a ceder. Vamos a ganar. Y vamos a ganar juntos.";
  const SAMPLE_TEXT_EN =
    "My fellow citizens: today we face a decisive hour. We are not alone: behind every decision we make there are enemies of the people working in the shadows to halt our advance. Fear will not stop what we have already begun. The greatness of this nation is in its hard-working people, not in those who occupy the offices and want to impose on us a foreign model. That is why I tell you: we will not yield. We will win. And we will win together.";

  const loadSample = () => {
    setTab("text");
    setText(lang === "en" ? SAMPLE_TEXT_EN : SAMPLE_TEXT_ES);
  };

  const generatePressKit = async (refresh = false) => {
    if (!analysisId) {
      toast.error(t("dash_min_chars"));
      return;
    }
    setPressLoading(true);
    try {
      const r = await api.post(`/analyses/${analysisId}/press-kit${refresh ? "?refresh=true" : ""}`);
      setPressKit(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setPressLoading(false);
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const q = search.trim();
      if (q.length >= 2) {
        // Full-text scored search
        const params = { q, limit: 50 };
        if (tagFilter) params.tag = tagFilter;
        const r = await api.get("/analyses/search", { params });
        setHistory(
          (r.data?.items || []).map((it) => ({
            ...it,
            // ensure fields downstream consumers expect
            tags: it.tags || [],
          })),
        );
      } else {
        // Default chronological list (no query)
        const params = {};
        if (tagFilter) params.tag = tagFilter;
        const r = await api.get("/analyses", { params });
        setHistory(r.data);
      }
    } catch (e) {
      console.warn("Failed to load history", e);
    }
  }, [search, tagFilter]);

  const loadTags = useCallback(async () => {
    try {
      const r = await api.get("/analyses/tags/all");
      setAllTags(r.data);
    } catch (e) {
      console.warn("Failed to load tags", e);
    }
  }, []);

  // debounce search
  useEffect(() => {
    const id = setTimeout(loadHistory, 250);
    return () => clearTimeout(id);
  }, [loadHistory]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const MAX_COMPARE = 6;
  const toggleCompare = (id) => {
    setCompareSet((curr) => {
      if (curr.includes(id)) return curr.filter((x) => x !== id);
      if (curr.length >= MAX_COMPARE) return curr; // ignore extra
      return [...curr, id];
    });
  };

  const runCompare = () => {
    if (compareSet.length < 2) return;
    nav(`/app/compare?ids=${compareSet.join(",")}`);
  };

  useEffect(() => {
    if (analysis && outRef.current) {
      outRef.current.scrollTop = outRef.current.scrollHeight;
    }
  }, [analysis]);

  const runAnalysis = async () => {
    if (text.trim().length < 20) {
      toast.error(t("dash_min_chars"));
      return;
    }
    setAnalysis("");
    setAnalysisId(null);
    setStreaming(true);
    try {
      await streamAnalysis(
        { text, model, language: lang, title: text.slice(0, 80) },
        {
          onMeta: (m) => setAnalysisId(m.id),
          onDelta: (c) => setAnalysis((prev) => prev + c),
          onDone: () => {
            setStreaming(false);
            loadHistory();
            toast.success("✓");
          },
          onError: (err) => {
            setStreaming(false);
            toast.error(err);
          },
        }
      );
    } catch (e) {
      setStreaming(false);
      toast.error(String(e));
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/files/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(r.data.text || "");
      setTab("text");
      toast.success(lang === "es" ? "Texto extraído" : "Text extracted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAudio = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("language", lang);
      const r = await api.post("/audio/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(r.data.text || "");
      setTab("text");
      toast.success(lang === "es" ? "Audio transcrito" : "Audio transcribed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteAnalysis = async (id) => {
    try {
      await api.delete(`/analyses/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("✓");
    } catch {
      toast.error("Error");
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard-page">
      <OnboardingModal />
      <AlertDialog
        open={!!pendingDeleteId}
        onOpenChange={(o) => !o && setPendingDeleteId(null)}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash_delete")}?</AlertDialogTitle>
            <AlertDialogDescription>{t("delete_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">
              {lang === "es" ? "Cancelar" : "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={() => pendingDeleteId && deleteAnalysis(pendingDeleteId)}
              className="bg-[#D90429] text-white hover:bg-[#0A0A0A]"
            >
              {t("dash_delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Header />
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
        {/* Sidebar — order 2 on mobile (form first), order 1 on desktop (left rail) */}
        <aside className="order-2 lg:order-1 border border-[#171717] bg-white p-6 h-fit lg:sticky lg:top-6" data-testid="history-panel">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-4">
            · HISTORIAL · {history.length.toString().padStart(2, "0")}
          </div>
          <h2 className="font-serif-display text-2xl font-bold mb-4 leading-none">
            {t("dash_history")}
          </h2>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
            <input
              data-testid="history-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("dash_search")}
              className="w-full border border-[#E5E5E5] focus:border-[#0A0A0A] bg-white pl-7 pr-2 py-2 text-xs font-mono-tech focus:outline-none"
            />
          </div>

          {/* Tag filter */}
          {allTags.length > 0 && (
            <div className="mb-4">
              <div className="font-mono-tech text-[9px] tracking-[0.25em] uppercase text-[#A3A3A3] mb-2">
                {t("dash_tags_filter")}
              </div>
              <div className="flex flex-wrap gap-1" data-testid="tag-filter">
                <button
                  data-testid="tag-filter-all"
                  onClick={() => setTagFilter("")}
                  className={`font-mono-tech text-[10px] uppercase tracking-widest px-2 py-1 border ${
                    tagFilter === "" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                  }`}
                >
                  {t("dash_all_tags")}
                </button>
                {allTags.map((tg) => (
                  <button
                    key={tg.tag}
                    data-testid={`tag-filter-${tg.tag}`}
                    onClick={() => setTagFilter(tg.tag === tagFilter ? "" : tg.tag)}
                    className={`font-mono-tech text-[10px] uppercase tracking-widest px-2 py-1 border ${
                      tagFilter === tg.tag ? "bg-[#D90429] text-white border-[#D90429]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                    }`}
                  >
                    #{tg.tag} <span className="opacity-60">{tg.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Compare bar */}
          {compareSet.length > 0 && (
            <div className="border border-[#D90429] bg-[#FFF5F5] p-2 mb-4" data-testid="compare-bar">
              <div className="font-mono-tech text-[9px] tracking-[0.25em] uppercase text-[#D90429] mb-2">
                · COMPARAR · {compareSet.length}/{MAX_COMPARE}
              </div>
              <div className="flex gap-1">
                <button
                  data-testid="compare-run-btn"
                  onClick={runCompare}
                  disabled={compareSet.length < 2}
                  className="flex-1 bg-[#0A0A0A] text-white font-mono-tech text-[10px] uppercase tracking-widest px-2 py-2 border border-[#0A0A0A] hover:bg-[#D90429] hover:border-[#D90429] disabled:opacity-40 inline-flex items-center justify-center gap-1"
                >
                  <GitCompareArrows className="w-3 h-3" />
                  {compareSet.length >= 3 ? `COMPARAR ${compareSet.length} →` : t("dash_compare_run")}
                </button>
                <button
                  data-testid="compare-clear-btn"
                  onClick={() => setCompareSet([])}
                  className="font-mono-tech text-[10px] uppercase px-2 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                  aria-label="clear"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {history.length === 0 ? (
            <p className="font-mono-tech text-xs text-[#A3A3A3] uppercase tracking-widest">
              {t("dash_no_history")}
            </p>
          ) : (
            <ul className="space-y-3 max-h-[55vh] overflow-y-auto subtle-scroll">
              {history.map((h) => {
                const selected = compareSet.includes(h.id);
                return (
                <li
                  key={h.id}
                  className={`border p-3 group transition-colors ${selected ? "border-[#D90429] bg-[#FFF5F5]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
                  data-testid={`history-item-${h.id}`}
                >
                  <div className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3] uppercase flex justify-between mb-2">
                    <span>{h.model.toUpperCase()}</span>
                    <span>{new Date(h.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="font-serif-display text-sm leading-snug mb-2 line-clamp-3">
                    {h.title}
                  </div>
                  {h.snippet && (
                    <div
                      data-testid={`history-snippet-${h.id}`}
                      className="font-sans-body text-[11px] text-[#525252] leading-relaxed mb-2 italic border-l-2 border-[#D90429] pl-2 line-clamp-2"
                    >
                      {highlightSnippet(h.snippet, search)}
                    </div>
                  )}
                  {typeof h.score === "number" && h.score > 0 && (
                    <div className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#D90429] mb-2">
                      · score {h.score.toFixed(2)}
                    </div>
                  )}
                  {h.tags && h.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {h.tags.slice(0, 4).map((tg) => (
                        <span key={tg} className="font-mono-tech text-[9px] uppercase tracking-widest bg-[#0A0A0A] text-white px-1.5 py-0.5">
                          #{tg}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 font-mono-tech text-[10px] uppercase tracking-widest">
                    <Link
                      to={`/app/analysis/${h.id}`}
                      data-testid={`view-${h.id}`}
                      className="px-2 py-1 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white inline-flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> {t("dash_view")}
                    </Link>
                    <button
                      data-testid={`compare-toggle-${h.id}`}
                      onClick={() => toggleCompare(h.id)}
                      className={`px-2 py-1 border inline-flex items-center gap-1 ${selected ? "border-[#D90429] bg-[#D90429] text-white" : "border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"}`}
                    >
                      <GitCompareArrows className="w-3 h-3" /> {selected ? "✓" : t("dash_compare_select")}
                    </button>
                    <button
                      data-testid={`delete-${h.id}`}
                      onClick={() => setPendingDeleteId(h.id)}
                      className="px-2 py-1 border border-[#0A0A0A] hover:bg-[#D90429] hover:text-white hover:border-[#D90429] inline-flex items-center gap-1 ml-auto"
                    >
                      <Trash2 className="w-3 h-3" /> {t("dash_delete")}
                    </button>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Main — order 1 on mobile (form first), order 2 on desktop (right side) */}
        <main className="order-1 lg:order-2 space-y-8">
          {/* Input section */}
          <section className="border border-[#171717] bg-white">
            <div className="flex border-b border-[#171717] font-mono-tech text-xs uppercase tracking-[0.25em]">
              {[
                { id: "text", I: FileText, label: t("dash_tab_text") },
                { id: "file", I: Upload, label: t("dash_tab_file") },
                { id: "audio", I: Mic, label: t("dash_tab_audio") },
              ].map((x) => (
                <button
                  key={x.id}
                  data-testid={`tab-${x.id}`}
                  onClick={() => setTab(x.id)}
                  className={`flex-1 px-4 py-4 border-r border-[#171717] last:border-r-0 inline-flex items-center justify-center gap-2 ${
                    tab === x.id ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F5F5F5]"
                  }`}
                >
                  <x.I className="w-4 h-4" />
                  {x.label}
                </button>
              ))}
            </div>

            <div className="p-6 md:p-8">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
                <h2 className="font-serif-display text-2xl md:text-3xl font-bold leading-none">
                  {t("dash_input_title")}
                </h2>
                <div className="flex items-center gap-4">
                  {tab === "text" && (
                    <button
                      data-testid="load-sample-btn"
                      type="button"
                      onClick={loadSample}
                      className="font-mono-tech text-[10px] tracking-widest uppercase text-[#0A0A0A] hover:text-[#D90429] border-b border-dashed border-[#A3A3A3] hover:border-[#D90429] pb-0.5"
                    >
                      ↓ {t("sample_text_btn")}
                    </button>
                  )}
                  <span className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3] uppercase">
                    {text.length} {t("dash_chars")}
                  </span>
                </div>
              </div>

              {tab === "text" && (
                <textarea
                  data-testid="input-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={10}
                  placeholder={t("dash_input_placeholder")}
                  className="w-full border border-[#0A0A0A] bg-[#FAFAFA] p-4 font-sans-body text-base leading-relaxed focus:outline-none focus:border-[#D90429] resize-y"
                />
              )}

              {tab === "file" && (
                <label
                  data-testid="file-upload-area"
                  className="border-2 border-dashed border-[#A3A3A3] hover:border-[#0A0A0A] bg-[#FAFAFA] p-12 flex flex-col items-center justify-center cursor-pointer transition-colors text-center"
                >
                  <Upload className="w-10 h-10 mb-4 stroke-[1.5]" />
                  <div className="font-serif-display text-2xl mb-2">{t("dash_upload_file")}</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    .txt · .pdf · .md
                  </div>
                  <input
                    data-testid="file-input"
                    type="file"
                    accept=".txt,.pdf,.md"
                    className="hidden"
                    onChange={handleFile}
                  />
                  {uploading && (
                    <Loader2 className="w-5 h-5 mt-4 animate-spin text-[#D90429]" />
                  )}
                </label>
              )}

              {tab === "audio" && (
                <label
                  data-testid="audio-upload-area"
                  className="border-2 border-dashed border-[#A3A3A3] hover:border-[#0A0A0A] bg-[#FAFAFA] p-12 flex flex-col items-center justify-center cursor-pointer transition-colors text-center"
                >
                  <Mic className="w-10 h-10 mb-4 stroke-[1.5]" />
                  <div className="font-serif-display text-2xl mb-2">{t("dash_transcribe")}</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    {t("dash_upload_audio")}
                  </div>
                  <input
                    data-testid="audio-input"
                    type="file"
                    accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                    className="hidden"
                    onChange={handleAudio}
                  />
                  {uploading && (
                    <Loader2 className="w-5 h-5 mt-4 animate-spin text-[#D90429]" />
                  )}
                </label>
              )}

              <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">
                    {t("dash_model")}
                  </label>
                  <div className="flex gap-0 border border-[#171717]" data-testid="model-selector">
                    {(availableModels.length > 0
                      ? availableModels
                      : MODELS.map((m) => ({ id: m.id, label: m.label, cost_estimated: null }))
                    ).map((m) => {
                      const isActive = model === m.id;
                      const costStr =
                        m.cost_estimated == null
                          ? lang === "es"
                            ? "coste n/d"
                            : "cost n/a"
                          : `~$${Number(m.cost_estimated).toFixed(4)}`;
                      return (
                        <button
                          key={m.id}
                          data-testid={`model-${m.id}`}
                          onClick={() => setModel(m.id)}
                          title={`${m.label} · ${costStr}`}
                          className={`px-4 py-2 font-mono-tech text-xs uppercase tracking-widest border-r border-[#171717] last:border-r-0 flex flex-col items-start gap-0.5 ${
                            isActive ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"
                          }`}
                        >
                          <span>{m.label}</span>
                          <span
                            data-testid={`model-${m.id}-cost`}
                            className={`text-[8px] tracking-[0.15em] normal-case ${
                              isActive ? "text-white/70" : "text-[#A3A3A3]"
                            }`}
                          >
                            {costStr}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="font-mono-tech text-[9px] tracking-widest uppercase text-[#A3A3A3] mt-1">
                    {t("cost_est_hint")}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button
                    data-testid="run-analysis-btn"
                    onClick={runAnalysis}
                    disabled={streaming || text.trim().length < 20}
                    className="bg-[#D90429] text-white px-8 py-4 font-mono-tech text-xs uppercase tracking-[0.3em] border border-[#D90429] hover:bg-[#0A0A0A] hover:border-[#0A0A0A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-3"
                  >
                    {streaming && <Loader2 className="w-4 h-4 animate-spin" />}
                    {streaming ? t("dash_running") : t("dash_run")} →
                  </button>
                  {!streaming && text.trim().length < 20 && (
                    <span
                      data-testid="run-paste-hint"
                      className="font-sans-body text-[10px] text-[#A3A3A3] italic"
                    >
                      {t("cta_paste_hint")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Output */}
          {(analysis || streaming) && (
            <section ref={outRef} className="max-h-[78vh] overflow-y-auto subtle-scroll">
              <AnalysisOutput text={analysis} streaming={streaming} />
            </section>
          )}

          {analysisId && !streaming && (
            <section className="flex flex-wrap items-center justify-between gap-4 border border-[#171717] bg-[#FAFAFA] p-5">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-1">
                  · LANZAR · MODO PRENSA
                </div>
                <div className="font-serif-display text-xl leading-tight">
                  {lang === "es"
                    ? "Convierte este análisis en un kit listo para publicar."
                    : "Turn this analysis into a publish-ready kit."}
                </div>
              </div>
              <button
                data-testid="open-press-kit-btn"
                onClick={() => generatePressKit(false)}
                disabled={pressLoading}
                className="bg-[#0A0A0A] text-white px-6 py-4 font-mono-tech text-xs uppercase tracking-[0.3em] border border-[#0A0A0A] hover:bg-[#D90429] hover:border-[#D90429] transition-colors disabled:opacity-40 inline-flex items-center gap-3"
              >
                {pressLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Newspaper className="w-4 h-4" />
                )}
                {pressLoading ? t("press_generating") : t("press_mode")} →
              </button>
            </section>
          )}
        </main>
      </div>

      {pressKit && (
        <PressKit
          kit={pressKit}
          refreshing={pressLoading}
          onRefresh={() => generatePressKit(true)}
          onClose={() => setPressKit(null)}
        />
      )}
    </div>
  );
}
